import { useCallback, useState, CSSProperties } from 'react';
import { JSONContent } from '@tiptap/react'
import Editor from '../../molecules/Editor';
import { WelcomeParams } from '../Welcome';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../configureStore';
import { IInstitution, updateInstitutionSteps } from '../../../redux/reducer/institution';
import { ConfigStepId, ConfigStepType, ConfigStepWelcome } from '../../../utils/configuration';
import { useMsal } from '@azure/msal-react';
import { Alert, Space, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '16px',
    },
    editorWrapper: {
        backgroundColor: '#fafafa',
        padding: '24px',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
    },
    infoSection: {
        marginBottom: '16px',
    },
};

interface AdminWelcomeParams extends WelcomeParams { }

const Welcome = ({
    jsonContent,
}: AdminWelcomeParams) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();

    const institution = useSelector<RootState, IInstitution | undefined>(({ institution }) => institution.testInstitution);

    const onSave = useCallback(async (json: JSONContent) => new Promise<void>((res) => {
        if (institution) {
            const cloned = structuredClone(institution.services);
            const idx = cloned.findIndex(({ id }: ConfigStepType) => id === ConfigStepId.welcome);
            const newStep: ConfigStepWelcome = cloned[idx] as ConfigStepWelcome;
            newStep.params = {
                ...newStep.params,
                jsonContent: json,
            };
            cloned[idx] = newStep;
            dispatch(updateInstitutionSteps({
                onSuccess: () => { res() },
                onFailure: () => { res() },
                input: {
                    id: institution?.id,
                    institutionId: institution?.institutionId,
                    services: cloned,
                },
                headers: {
                    Authorization: `Bearer ${accounts[0].idToken}`,
                },
            }, 'params'))
        } else { res(); }
    }), [institution]);

    const [saving, setSaving] = useState(false);

    const handleSave = useCallback(async (json: JSONContent) => {
        setSaving(true);
        try {
            await onSave(json);
        } finally {
            setSaving(false);
        }
    }, [onSave]);

    return (
        <div style={styles.container}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    style={styles.infoSection}
                    message="Welcome Message Configuration"
                    description="This message will be displayed to users when they first access the application. Use it to provide instructions, set expectations, or welcome them to the process."
                    type="info"
                    showIcon
                    closable
                    icon={<InfoCircleOutlined />}
                />

                <div style={styles.editorWrapper}>
                    <Editor
                        title=''
                        content={jsonContent}
                        onSave={handleSave}
                        loading={saving}
                        setLoading={setSaving}
                        institution={institution}
                    />
                </div>

                <Space size="small">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    <Text type="secondary">
                        Tip: You can use formatting options like headings, lists, and links to make your welcome message more engaging.
                    </Text>
                </Space>
            </Space>
        </div>
    );
};

export default Welcome;
